// Functions that build and compare a single node's data. This file doesn't import `nodes/registry`,
// so node types can import it without an import cycle.

import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import type { LBNodeDataWithIndexer, ReactFlowNode } from './types';

/**
 * Compares two LBNodeData objects for equality.
 * Uses JSON serialization for deep comparison.
 *
 * @param a - First LBNodeData object
 * @param b - Second LBNodeData object
 * @returns true if the objects are equal, false otherwise
 */
export function isLBNodeDataEqual(
  a: LBNodeData | undefined,
  b: LBNodeData | undefined
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Returns a minimal valid LBNodeData with no oneof assigned.
 *
 * When new required top-level fields are added to LBNodeData, set their
 * defaults here so callers don't have to.
 */
export function emptyNodeData(): LBNodeDataWithIndexer {
  return { user_variables: [] };
}

/** Resolves to K, failing to compile if any listed key is missing from T. */
type KeysOf<T, K extends keyof T> = K;

/** Top-level LBNodeData fields that are not part of the proto `data` oneof. */
type LBNodeDataNonOneofKey = KeysOf<
  LBNodeData,
  'user_variables' | 'custom_display_name'
>;

/** The proto `data` oneof keys. `buildNodeData` accepts only these. */
type LBNodeDataOneofKey = Exclude<keyof LBNodeData, LBNodeDataNonOneofKey>;

/**
 * Returns an LBNodeData with the given oneof key set to `data`, and the other
 * top-level fields set to their defaults.
 */
export function buildNodeData<K extends LBNodeDataOneofKey>(
  key: K,
  data: NonNullable<LBNodeData[K]>
): LBNodeDataWithIndexer {
  return { ...emptyNodeData(), [key]: data };
}

/**
 * Whether a node is the read-only AI Agent mirror materialized beneath a
 * Transfer To node's "Connect to an agent" fallback. It is coupled to that
 * transfer node — it can't be deleted independently, only as part of
 * deleting the transfer node (which cascades to its descendants).
 */
export function isTransferEscalationMirror(node: ReactFlowNode): boolean {
  const data = node.data as LBNodeData | undefined;
  return data?.ai_call_action?.is_transfer_escalation === true;
}
